import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { formatDate, titleCase } from "../../lib/format";
import { PRODUCT_NAME } from "../../product";
import {
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";
import { useVenues } from "../venues/queries";

interface CalendarFeed {
  id: string;
  name: string;
  membershipId?: string;
  venueId?: string;
  mode: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt: string;
  token?: string;
  feedUrl?: string;
}

// A private calendar link belongs to the person who uses it, not to whoever administers the
// workspace, so it stays in settings behind the same permission as the calendar itself. Filing it
// with the integrations an administrator configures would take it away from most of the staff who
// actually subscribe to it.
export function CalendarFeedSettings() {
  const auth = useAuth();
  const canCalendar = membershipAllows(auth.session?.selectedMembership, "calendar.view");
  const feeds = useQuery({
    queryKey: ["messaging", "calendar-feeds"],
    queryFn: () => api<ListResponse<CalendarFeed>>("/api/v1/messaging/calendar-feeds"),
    enabled: canCalendar,
  });
  const venues = useVenues();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [newFeed, setNewFeed] = useState<CalendarFeed | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<CalendarFeed | null>(null);

  async function createFeed(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const expiresAt = String(form.get("expiresAt"));
    setWorking(true);
    setError(undefined);
    setNewFeed(null);
    try {
      const item = await api<CalendarFeed>("/api/v1/messaging/calendar-feeds", {
        method: "POST",
        body: jsonBody({
          name: String(form.get("name")),
          membershipId: null,
          venueId: String(form.get("venueId")) || null,
          mode: String(form.get("mode")),
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        }),
      });
      setNewFeed(item);
      formElement.reset();
      await feeds.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function revokeFeed(item: CalendarFeed) {
    setWorking(true);
    setError(undefined);
    try {
      await api<void>(`/api/v1/messaging/calendar-feeds/${item.id}`, { method: "DELETE" });
      setNewFeed(null);
      await feeds.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
      setRevokeTarget(null);
    }
  }

  if ((canCalendar && feeds.isLoading) || venues.isLoading) {
    return <PanelSkeleton />;
  }
  return (
    <div className="stack">
      {error || feeds.error || venues.error ? (
        <ErrorPanel error={error ?? feeds.error ?? venues.error} />
      ) : null}
      <Card>
        <CardHeader
          title="Private calendar links"
          description="Create a private link for the venues you can access. You can turn it off at any time."
        />
        {canCalendar ? (
          <>
            <form className="form-grid" onSubmit={createFeed}>
              <Field label="Calendar name">
                <input name="name" required defaultValue={`My ${PRODUCT_NAME} calendar`} />
              </Field>
              <Field label="Detail level">
                <select name="mode" defaultValue="operations">
                  <option value="busy">Busy only</option>
                  <option value="operations">Booking and location</option>
                  <option value="staff">Include customer name</option>
                </select>
              </Field>
              <Field label="Venue">
                <select name="venueId" defaultValue="">
                  <option value="">All venues available to you</option>
                  {venues.data?.items.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Stop sharing after"
                hint="Optional. Leave blank to use the standard expiry period."
              >
                <input name="expiresAt" type="datetime-local" />
              </Field>
              <div className="full">
                <FormActions submitting={working} submitLabel="Create calendar link" />
              </div>
            </form>
            {newFeed?.feedUrl ? (
              <Notice tone="warning" title="Copy this private link now">
                <a className="mono" href={newFeed.feedUrl}>
                  {newFeed.feedUrl}
                </a>
              </Notice>
            ) : null}
            {feeds.data?.items.length ? (
              <DataTable
                caption="Private calendar links"
                items={feeds.data.items}
                getKey={(item) => item.id}
                columns={[
                  { label: "Name", render: (item) => item.name },
                  { label: "Details", render: (item) => titleCase(item.mode) },
                  { label: "Stops sharing", render: (item) => formatDate(item.expiresAt) },
                  {
                    label: "Status",
                    render: (item) => <StatusPill value={item.revokedAt ? "revoked" : "active"} />,
                  },
                  {
                    label: "Action",
                    render: (item) =>
                      item.revokedAt ? (
                        "Revoked"
                      ) : (
                        <button
                          className="button danger small"
                          type="button"
                          disabled={working}
                          onClick={() => setRevokeTarget(item)}
                        >
                          Turn off
                        </button>
                      ),
                  },
                ]}
              />
            ) : (
              <EmptyState
                title="No calendar links"
                description="Create a private link to add bookings to Google Calendar, Outlook, or Apple Calendar."
              />
            )}
          </>
        ) : (
          <Notice tone="warning">Your role cannot view the booking calendar.</Notice>
        )}
      </Card>
      <ConfirmDialog
        open={revokeTarget !== null}
        title="Turn off calendar link?"
        description={
          revokeTarget
            ? `"${revokeTarget.name}" will stop updating immediately. Sharing it again will require a new private link.`
            : "Existing subscriptions will stop updating immediately."
        }
        confirmLabel="Turn off link"
        submitting={working}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revokeTarget) void revokeFeed(revokeTarget);
        }}
      />
    </div>
  );
}
